// Models
import { PeerStats } from 'src/app/models/peer-stats/peer-stats';
import { PeerStatsParticipant } from 'src/app/models/peer-stats/peer-stats-participant';

// Services
import { LoggingChannels, LoggingService } from 'src/app/services/logging/logging.service';
import { ParticipantsListService } from '../participants-list/participants-list.service';
import { VirtualBackgroundService } from '../virtual-background/virtual-background.service';

export class StatsReporter {
  constructor(
    private background: VirtualBackgroundService,
    private logging: LoggingService,
    private participants: ParticipantsListService,
  ) {}

  getReport(report: PeerStats, userId: string): any[] {
    const statsReports = [];

    statsReports.push({
      session: {
        availableBandwidth: ((report.availableOutgoingBitrate || 0) / 1024).toFixed(2),
        bandwidthIn: report.incomingBitrate?.toFixed(2) || 0,
        bandwidthOut: report.outgoingBitrate?.toFixed(2) || 0,
        stVolatility: report.stVolatility?.toFixed(2) || 0,
        dtVolatility: report.dtVolatility?.toFixed(2) || 0,
        echoReturnLoss: report.echoReturnLoss || 0,
        echoReturnLossEnhancement: report.echoReturnLossEnhancement || 0,
        audio: this.participants.participants[userId].audioSwitch,
        video: this.participants.participants[userId].videoSwitch,
        background: this.background.isActive,
      }
    });

    this.participants.participantsArray.forEach((participant) => {
      const r = report.participantsStats[participant.id] || new PeerStatsParticipant(participant.id);
      this.pushReport(statsReports, participant.id, r);
    });
    Object.keys(report.shareStats).forEach((id) => {
      this.pushReport(statsReports, id, report.shareStats[id], true);
    });

    return statsReports;
  }

  pushReport(statsReports: any[], id: string, p: PeerStatsParticipant, sharing = false): void {
    const r = {
      session: {
        id: '',
        sharing,
        outgoingBitrate: 0,
        incomingBitrate: 0,
        rtt: 0,
        avDelay: 0,
      },
      status: {
        dataChannel: '',
        remoteDataChannel: '',
        connection: '',
        remoteConnection: '',
        iceConnection: '',
        remoteIceConnection: '',
      },
      audio: {
        packetsLost: 0,
        jitterIn: 0,
        jitterOut: 0,
        outgoingBitrate: 0,
        incomingBitrate: 0,
      },
      video: {
        packetsLost: 0,
        encodeTimePerFrame: 0,
        decodeTimePerFrame: 0,
        frameHeightIn: 0,
        frameWidthIn: 0,
        frameHeightOut: 0,
        frameWidthOut: 0,
        jitterIn: 0,
        jitterOut: 0,
        fpsIn: 0,
        fpsOut: 0,
        outgoingBitrate: 0,
        incomingBitrate: 0,
      },
    };
    r.session.id = id;
    r.status.dataChannel = this.participants.participants[id]?.dataChannel?.readyState || '';
    r.status.remoteDataChannel = this.participants.participants[id]?.dataChannel?.remoteReadyState || '';
    r.status.connection = this.participants.participants[id]?.pc?.connectionState || '';
    r.status.remoteConnection = this.participants.participants[id]?.pc?.remoteConnectionState || '';
    r.status.iceConnection = this.participants.participants[id]?.pc?.iceConnectionState || '';
    r.status.remoteIceConnection = this.participants.participants[id]?.pc?.remoteIceConnectionState || '';
    if (typeof p.rtt !== 'undefined') {
      r.session.rtt = p.rtt;
    }
    if (typeof p.avDelay !== 'undefined') {
      r.session.avDelay = p.avDelay;
    }
    if (typeof p.outgoingBitrate !== 'undefined') {
      r.session.outgoingBitrate = p.outgoingBitrate;
    }
    if (typeof p.incomingBitrate !== 'undefined') {
      r.session.incomingBitrate = p.incomingBitrate;
    }

    if (typeof p.audio.packetsLostPerc !== 'undefined') {
      r.audio.packetsLost = p.audio.packetsLostPerc;
    }
    if (typeof p.audio.jitterIn !== 'undefined') {
      r.audio.jitterIn = p.audio.jitterIn;
    }
    if (typeof p.audio.jitterOut !== 'undefined') {
      r.audio.jitterOut = p.audio.jitterOut;
    }
    if (typeof p.audio.outgoingBitrate !== 'undefined') {
      r.audio.outgoingBitrate = p.audio.outgoingBitrate;
    }
    if (typeof p.audio.incomingBitrate !== 'undefined') {
      r.audio.incomingBitrate = p.audio.incomingBitrate;
    }

    if (typeof p.video.packetsLostPerc !== 'undefined') {
      r.video.packetsLost = p.video.packetsLostPerc;
    }
    if (typeof p.video.encodeTimePerFrame !== 'undefined') {
      r.video.encodeTimePerFrame = p.video.encodeTimePerFrame;
    }
    if (typeof p.video.decodeTimePerFrame !== 'undefined') {
      r.video.decodeTimePerFrame = p.video.decodeTimePerFrame;
    }
    if (typeof p.video.frameWidthIn !== 'undefined') {
      r.video.frameWidthIn = p.video.frameWidthIn;
    }
    if (typeof p.video.frameHeightIn !== 'undefined') {
      r.video.frameHeightIn = p.video.frameHeightIn;
    }
    if (typeof p.video.frameWidthOut !== 'undefined') {
      r.video.frameWidthOut = p.video.frameWidthOut;
    }
    if (typeof p.video.frameHeightOut !== 'undefined') {
      r.video.frameHeightOut = p.video.frameHeightOut;
    }
    if (typeof p.video.jitterIn !== 'undefined') {
      r.video.jitterIn = p.video.jitterIn;
    }
    if (typeof p.video.jitterOut !== 'undefined') {
      r.video.jitterOut = p.video.jitterOut;
    }
    if (typeof p.video.fpsIn !== 'undefined') {
      r.video.fpsIn = p.video.fpsIn;
    }
    if (typeof p.video.fpsOut !== 'undefined') {
      r.video.fpsOut = p.video.fpsOut;
    }
    if (typeof p.video.outgoingBitrate !== 'undefined') {
      r.video.outgoingBitrate = p.video.outgoingBitrate;
    }
    if (typeof p.video.incomingBitrate !== 'undefined') {
      r.video.incomingBitrate = p.video.incomingBitrate;
    }

    statsReports.push(r);
  }

  sendStats(report: PeerStats, userId: string, roomId: string): void {
    this.logging.log('CallStatsService: send', LoggingChannels.CallStats);
    const callStatsReport = this.getReport(report, userId);
    if (callStatsReport.length > 0) {
      this.logging.log('CallStatsService: sending', LoggingChannels.CallStats);
      this.logging.logCustomEvent('stats', {
        userId,
        roomId,
        report: callStatsReport,
      });
    }
  }
}
